import {
  ApprovalType,
  CustomFieldStatus,
  IAdminResponse,
  IApproval,
  ICustomFieldResponse,
  IGroupMembershipResponse,
  IGroupResponse,
  IRiderResponse,
} from '@sparelabs/api-client'
import {
  FormStatus,
  IAppointmentResponse,
  IAppointmentTypeResponse,
  ICaseFormResponse,
  ICaseResponse,
  IFormResponse,
} from '@sparelabs/engage-client'
import { GenericUtils } from '@sparelabs/generic-utils'
import { MINUTE } from '@sparelabs/time'
import { orderBy, pick, startCase, uniqBy } from 'lodash'
import moment from 'moment-timezone'
import { st } from '../locales/TranslationHelper'
import {
  IMarkdownCustomFieldFormatterOptions,
  MarkdownCustomFieldFormatter,
} from '../variables/MarkdownCustomFieldFormatter'
import {
  IMarkdownVariable,
  IMarkdownVariableDefinition,
  IMarkdownVariableDefinitionValue,
  MarkdownVariableDefinitionType,
} from '../variables/MarkdownVariableTypes'

// eslint-disable-next-line id-length
const tLabels = st.engageLetterLabels
const tHelpers = st.genericHelpers

// Define which fields from the rider object are available
const RIDER_FIELDS = ['firstName', 'lastName', 'email', 'phoneNumber', 'externalNumericId'] as const

interface IFormWithFields extends IFormResponse {
  fields: ICustomFieldResponse[]
}

interface IRiderWithMemberships extends Pick<IRiderResponse, (typeof RIDER_FIELDS)[number] | 'metadata'> {
  groupMemberships: IGroupMembershipResponse[]
}

export interface IAppointmentTypeWithForm extends IAppointmentTypeResponse {
  form: IFormWithFields | null
}

export interface IAppointmentWithType extends IAppointmentResponse {
  appointmentType: IAppointmentTypeResponse | null
}

interface ICaseWithRelations extends ICaseResponse {
  assignedAdmin: IAdminResponse | null
  rider: IRiderWithMemberships
  caseForms: ICaseFormResponse[]
  appointments: IAppointmentWithType[]
}

// Data for building definitions / labels
export interface IEngageLetterVariableBuilderData {
  forms: IFormWithFields[]
  riderCustomFields: ICustomFieldResponse[]
  groups: IGroupResponse[]
  groupCustomFields: ICustomFieldResponse[]
  isConditionalEligibilityEnabled: boolean
  appointmentTypes: IAppointmentTypeWithForm[]
}

// Data for generating the letter
export interface IEngageLetterVariableRenderData {
  caseData: ICaseWithRelations
  forms: IFormWithFields[]
  riderCustomFields: ICustomFieldResponse[]
  groupCustomFields: ICustomFieldResponse[]
  formatOptions: IMarkdownCustomFieldFormatterOptions
  appointmentTypes: IAppointmentTypeWithForm[]
}

type ILetterVariable = IMarkdownVariable<IEngageLetterVariableBuilderData, IEngageLetterVariableRenderData>

const RiderVar: ILetterVariable = {
  buildDefinition: ({ riderCustomFields }) => {
    const values: IMarkdownVariableDefinitionValue[] = RIDER_FIELDS.map((field) => ({
      key: `rider.${field}`,
      label: startCase(field),
    }))

    for (const field of riderCustomFields.filter((field) => field.status === CustomFieldStatus.Active)) {
      // TODO deal with archived fields
      values.push({
        key: `rider.${field.key}`,
        label: startCase(field.label),
      })
    }

    return [{ key: 'rider', groupLabel: 'Rider', values }]
  },

  buildData: async ({ caseData, riderCustomFields, formatOptions }) => {
    const riderMetadata = await MarkdownCustomFieldFormatter.format(
      riderCustomFields,
      caseData.rider.metadata,
      formatOptions,
    )
    return { rider: { ...pick(caseData.rider, RIDER_FIELDS), ...riderMetadata } }
  },
}

const isApprovalApproved = (approval: IApproval): boolean => approval.fields.max === null
const isApprovalRequired = (approval: IApproval): boolean => approval.fields.min === 1 && isApprovalApproved(approval)

const GroupMembershipVar: ILetterVariable = {
  buildDefinition: ({ groups, groupCustomFields, isConditionalEligibilityEnabled }) => {
    const values: IMarkdownVariableDefinition[] = []

    for (const group of groups) {
      const customFieldValues = groupCustomFields
        .filter((field) => field.status === CustomFieldStatus.Active)
        .map((field) => ({
          key: `group.${group.id}.${field.key}`,
          label: field.label,
        }))
      values.push({
        key: `group.${group.id}`,
        groupLabel: group.name.toLowerCase().includes('group') ? group.name : `${group.name} Group`,
        values: [
          { key: `group.${group.id}.startTs`, label: 'Start Date' },
          { key: `group.${group.id}.expiryTs`, label: 'Expiry Date' },
          ...(isConditionalEligibilityEnabled
            ? [
                { key: `group.${group.id}.conditionIds`, label: 'Conditions' },
                { key: `group.${group.id}.approvedRiderTypes`, label: 'Approved Rider Types' },
                { key: `group.${group.id}.requiredRiderTypes`, label: 'Required Rider Types' },
                { key: `group.${group.id}.approvedAccessibilityFeatures`, label: 'Approved Accessibility Features' },
                { key: `group.${group.id}.requiredAccessibilityFeatures`, label: 'Required Accessibility Features' },
              ]
            : []),
          ...customFieldValues,
        ],
      })
    }

    return values
  },

  buildData: async ({ caseData, groupCustomFields, formatOptions }) => {
    const groupVariableMap: Record<string, Record<string, unknown>> = {}

    // We can only access one group membership per group, so we need to dedupe. Use the newest created membership for each group.
    const uniqueGroupMemberships = uniqBy(orderBy(caseData.rider.groupMemberships, 'createdAt', 'desc'), 'groupId')

    for (const membership of uniqueGroupMemberships) {
      const groupMetadata = await MarkdownCustomFieldFormatter.format(
        groupCustomFields,
        membership.metadata,
        formatOptions,
      )

      const approvedRiderTypes = membership.approvals
        .filter((approval) => approval.type === ApprovalType.RiderType && isApprovalApproved(approval))
        .map((approval) => approval.fields.type)
      const requiredRiderTypes = membership.approvals
        .filter((approval) => approval.type === ApprovalType.RiderType && isApprovalRequired(approval))
        .map((approval) => approval.fields.type)
      const approvedAccessibilityFeatures = membership.approvals
        .filter((approval) => approval.type === ApprovalType.AccessibilityFeature && isApprovalApproved(approval))
        .map((approval) => approval.fields.type)
      const requiredAccessibilityFeatures = membership.approvals
        .filter((approval) => approval.type === ApprovalType.AccessibilityFeature && isApprovalRequired(approval))
        .map((approval) => approval.fields.type)

      groupVariableMap[membership.groupId] = {
        ...groupMetadata,
        startTs: membership.startTs ? moment(membership.startTs * 1000).format('LL') : null,
        expiryTs: membership.expiryTs ? moment(membership.expiryTs * 1000).format('LL') : null,
        conditionIds: membership.groupConditionIds,
        approvedRiderTypes,
        requiredRiderTypes,
        approvedAccessibilityFeatures,
        requiredAccessibilityFeatures,
      }
    }

    return { group: groupVariableMap }
  },
}

const AppointmentVar: ILetterVariable = {
  buildDefinition: ({ appointmentTypes }) => {
    const values: IMarkdownVariableDefinition[] = []

    for (const type of appointmentTypes) {
      const customFieldValues = type.form?.fields
        .filter((field) => field.status === CustomFieldStatus.Active)
        .map((field) => ({
          key: `appointment.${type.id}.${field.key}`,
          label: field.label,
        }))

      values.push({
        key: `appointment.${type.id}`,
        groupLabel: type.title,
        values: [
          { key: `appointment.${type.id}.startTs`, label: 'Appointment Start Date Time' },
          { key: `appointment.${type.id}.endTs`, label: 'Appointment End Date Time' },
          { key: `appointment.${type.id}.location`, label: 'Appointment Location' },
          { key: `appointment.${type.id}.durationMin`, label: 'Appointment Duration (Minutes)' },
          { key: `appointment.${type.id}.description`, label: 'Appointment Description' },
          ...(customFieldValues ?? []),
        ],
      })
    }

    return values
  },

  buildData: async ({ caseData, formatOptions, appointmentTypes }) => {
    const appointmentVariableMap: Record<string, Record<string, unknown>> = {}
    const uniqueAppointments = uniqBy(orderBy(caseData.appointments, 'createdAt', 'desc'), 'appointmentTypeId')

    for (const appointment of uniqueAppointments) {
      const caseForm = caseData.caseForms.find((caseForm) => caseForm.id === appointment.caseFormId)
      const fields = appointmentTypes.find((type) => type.id === appointment.appointmentTypeId)?.form?.fields
      const appointmentMetadata = await MarkdownCustomFieldFormatter.format(
        fields ?? [],
        caseForm?.metadata ?? {},
        formatOptions,
      )

      appointmentVariableMap[appointment.appointmentTypeId] = {
        ...appointmentMetadata,
        startTs: appointment.startTs ? moment.unix(appointment.startTs).tz(formatOptions.timezone).format('LLL') : null,
        endTs: appointment.endTs ? moment.unix(appointment.endTs).tz(formatOptions.timezone).format('LLL') : null,
        location: appointment.appointmentType?.location,
        durationMin: appointment.appointmentType?.duration
          ? `${Math.round(appointment.appointmentType.duration / MINUTE)}`
          : null,
        description: appointment.appointmentType?.description,
      }
    }

    return { appointment: appointmentVariableMap }
  },
}

const DateVar: ILetterVariable = {
  buildDefinition: () => [
    {
      key: 'date',
      groupLabel: tLabels.date.singularName(),
      values: [
        { key: 'date.current', label: tLabels.date.currentDate() },
        { key: 'date.currentYYYYMMDD', label: 'Current Date (YYYYMMDD)' },
      ],
    },
  ],
  buildData: ({ formatOptions }) => ({
    date: {
      current: moment().tz(formatOptions.timezone).format('LL'),
      currentYYYYMMDD: moment().tz(formatOptions.timezone).format('YYYYMMDD'),
    },
  }),
}

const FormsVar: ILetterVariable = {
  buildDefinition: ({ forms }) =>
    forms
      .filter((form) => form.status === FormStatus.Active)
      .map((form) => ({
        key: form.key,
        groupLabel: form.name,
        values: form.fields
          .filter((field) => field.status === CustomFieldStatus.Active)
          .map((field) => ({
            key: `forms.${form.key}.${field.key}`,
            label: field.label,
          })),
      })),
  buildData: async ({ caseData, forms, formatOptions }) => {
    const formVariables: Record<string, unknown> = {}
    for (const form of forms) {
      const data = caseData.caseForms.find((caseForm) => caseForm.formId === form.id)?.metadata || {}
      formVariables[form.key] = await MarkdownCustomFieldFormatter.format(form.fields, data, formatOptions)
    }
    return { forms: formVariables }
  },
}

const CaseVar: ILetterVariable = {
  buildDefinition: () => [
    {
      key: 'case',
      groupLabel: tLabels.case.singularName(),
      values: [
        {
          key: 'case.id',
          label: tLabels.case.id(),
        },
        {
          key: 'assignee.name',
          label: tLabels.case.assignedTo(),
        },
      ],
    },
  ],
  buildData: ({ caseData }) => {
    const name = `${caseData.assignedAdmin?.firstName} ${caseData.assignedAdmin?.lastName}`.trim()
    return { case: { id: caseData.id }, assignee: { name } }
  },
}

const GenericVar: ILetterVariable = {
  buildDefinition: () => {
    const insertAs = MarkdownVariableDefinitionType.Logic

    return [
      {
        key: 'logicUtils',
        groupLabel: tLabels.genericHelpers.logicUtils(),
        values: [
          { key: 'eq value1 value2', label: tHelpers.eq(), insertAs },
          { key: 'uneq value1 value2', label: tHelpers.uneq(), insertAs },
          { key: 'lt value1 value2', label: tHelpers.lt(), insertAs },
          { key: 'lte value1 value2', label: tHelpers.lte(), insertAs },
          { key: 'gt value1 value2', label: tHelpers.gt(), insertAs },
          { key: 'gte value1 value2', label: tHelpers.gte(), insertAs },
        ],
      },
      {
        key: 'arrayUtils',
        groupLabel: tLabels.genericHelpers.arrayUtils(),
        values: [
          { key: 'length list_or_array', label: tHelpers.length(), insertAs },

          { key: 'includes list_or_array value', label: tHelpers.includes(), insertAs },
        ],
      },
      {
        key: 'stringUtils',
        groupLabel: tLabels.genericHelpers.stringUtils(),
        values: [
          { key: 'lowercase value', label: tHelpers.lowercase(), insertAs },

          { key: 'uppercase value', label: tHelpers.uppercase(), insertAs },

          { key: 'contains string substring', label: tHelpers.contains(), insertAs },

          { key: 'concat separator string1 string2 ...', label: tHelpers.concat(), insertAs },

          { key: 'substring string direction count', label: tHelpers.substring(), insertAs },

          { key: 'truncate string maxLength suffix', label: tHelpers.truncate(), insertAs },
        ],
      },
    ]
  },
  buildHelpers: () => ({
    eq: GenericUtils.eq,
    uneq: GenericUtils.uneq,
    lt: GenericUtils.lt,
    lte: GenericUtils.lte,
    gt: GenericUtils.gt,
    gte: GenericUtils.gte,

    length: GenericUtils.length,
    includes: GenericUtils.includes,

    lowercase: GenericUtils.lowercase,
    uppercase: GenericUtils.uppercase,
    contains: GenericUtils.contains,
    concat: GenericUtils.concat,
    substring: GenericUtils.substring,
    truncate: GenericUtils.truncate,
  }),
}

export const EngageLetterVariables: ILetterVariable[] = [
  RiderVar,
  GroupMembershipVar,
  DateVar,
  FormsVar,
  CaseVar,
  AppointmentVar,
  GenericVar,
]
